<template>
  <v-container fluid class="pa-4">
    <v-row align="center" class="mb-4">
      <v-col class="text-right">
        <v-btn
          color="primary"
          @click="$router.push({ name: 'GoPagesCreate' })"
        >
          <v-icon left>mdi-plus</v-icon>
          Create Page
        </v-btn>
      </v-col>
    </v-row>

    <v-row v-if="loading">
      <v-col cols="12" class="text-center pa-5">
        <v-progress-circular
          indeterminate
          color="primary"
        ></v-progress-circular>
      </v-col>
    </v-row>
    <v-flex
      v-else-if="pages.length === 0"
      fluid
      class="whiteBackGround customBorderRadius"
    >
      <first-page
        :img-src="require('@/assets/goimage.svg')"
        :points="points"
        headline="Effortless Lead Generation with Custom Landing Pages"
        btn-url="/gopagesv2/create"
      ></first-page>
    </v-flex>
    <v-row v-else>
      <v-col
        v-for="item in pages"
        :key="item.id"
        cols="12"
        sm="6"
        md="4"
        lg="3"
      >
        <v-card
          class="modern-card customBorderRadius d-flex flex-column h-100"
          elevation="0"
          style="cursor: pointer; transition: all 0.2s ease; border: 1px solid #e0e0e0;"
          hover
          @click="editPage(item)"
        >
          <div
            class="card-header pb-2 px-4 pt-4 d-flex justify-space-between align-start"
          >
            <div class="d-flex flex-column" style="max-width: 70%;">
              <span
                class="text-truncate title font-weight-bold dotted-gradient"
                :title="item.businessName"
                style="cursor: pointer;"
                @click.stop="editPage(item)"
                >{{ item.businessName || "Untitled Page" }}</span
              >
              <span class="text-caption grey--text mt-1"
                >Created: {{ formatDate(item.createdOn) }}</span
              >
            </div>
            <v-chip
              small
              :color="item.status === 'published' ? 'green' : 'grey'"
              dark
            >
              {{ item.status || "draft" }}
            </v-chip>
          </div>

          <v-card-text class="flex-grow-1 px-4 mt-2">
            <div class="d-flex align-center grey--text text--darken-1 mb-4">
              <v-icon small class="mr-2">mdi-link-variant</v-icon>
              <a
                v-if="item.status === 'published'"
                :href="pLink(item)"
                target="_blank"
                class="text-truncate text-decoration-none text-body-1"
                @click.stop
                >{{ pLink(item) }}</a
              >
              <span
                v-else
                class="text-truncate text-body-1 grey--text"
                style="pointer-events: none;"
                >{{ pLink(item) }}</span
              >
            </div>

            <v-row class="mt-4 text-center align-center justify-space-around">
              <v-col cols="6" class="px-0">
                <div class="grey--text text-caption mb-1">Views</div>
                <div
                  class="font-weight-bold text--primary headline"
                  style="font-size: 24px;"
                >
                  {{
                    item.analytics && item.analytics.views
                      ? item.analytics.views
                      : item.views || 0
                  }}
                </div>
              </v-col>
              <v-col cols="6" class="px-0">
                <div class="grey--text text-caption mb-1">Leads</div>
                <div
                  class="font-weight-bold primary--text headline"
                  style="font-size: 24px;"
                >
                  {{
                    item.analytics && item.analytics.leads
                      ? item.analytics.leads
                      : item.totalLeads || item.leads || 0
                  }}
                </div>
              </v-col>
            </v-row>
          </v-card-text>

          <v-divider></v-divider>

          <v-card-actions
            class="px-4 py-3 grey lighten-4"
            style="border-bottom-left-radius: 12px; border-bottom-right-radius: 12px; overflow: hidden;"
          >
            <v-btn
              text
              small
              color="primary"
              class="text-capitalize px-2 mr-2"
              style="border-radius: 6px;"
              @click.stop="viewResults(item)"
            >
              <v-icon left small>mdi-format-list-bulleted</v-icon> Leads
            </v-btn>
            <v-btn
              text
              small
              color="secondary"
              class="text-capitalize px-2"
              style="border-radius: 6px;"
              @click.stop="viewAnalytics(item)"
            >
              <v-icon left small>mdi-chart-line</v-icon> Analytics
            </v-btn>
            <v-spacer></v-spacer>

            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  class="mx-1"
                  :href="pLink(item)"
                  target="_blank"
                  v-bind="attrs"
                  v-on="on"
                  @click.stop
                >
                  <v-icon small color="blue-grey">mdi-open-in-new</v-icon>
                </v-btn>
              </template>
              <span>Preview</span>
            </v-tooltip>

            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  class="mx-1"
                  v-bind="attrs"
                  @click.stop="deletePage(item)"
                  v-on="on"
                >
                  <v-icon small color="red lighten-1">mdi-delete</v-icon>
                </v-btn>
              </template>
              <span>Delete</span>
            </v-tooltip>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { mapGetters } from "vuex";
import firstPage from "@/components/firstPage.vue";
import fb from "../js/firebase.js";

export default {
  name: "GoPagesV2List",
  components: {
    firstPage,
  },
  data() {
    return {
      points: [
        "Design Custom Pages in Minutes: No coding required! Build high-converting landing pages with our intuitive drag-and-drop interface.",
        "Capture the Data You Need: Collect the specific information crucial for your sales process with customizable forms.",
        "Seamless Lead Management: Data from GoPages automatically syncs to GoDial lists, ensuring your telecallers have qualified leads ready for contact.",
      ],
      loading: false,
      pages: [],
      headers: [
        { text: "Name", value: "businessName" },
        { text: "Status", value: "status", width: 120 },
        { text: "Leads", value: "totalLeads" },
        { text: "Slug", value: "slug" },
        { text: "Created", value: "createdOn", width: 150 },
        { text: "Actions", value: "actions", sortable: false, width: 120 },
      ],
    };
  },
  computed: {
    ...mapGetters(["ENDPOINT"]),
  },
  created() {
    this.fetchPages();
  },
  methods: {
    fetchPages() {
      this.loading = true;
      var companyId =
        this.$store.state.companyDetails.id ||
        this.$store.state.companyDetails._id;
      this.$http
        .get(`${this.ENDPOINT}/gopages/getByCompany`, {
          params: { companyId: companyId },
        })
        .then((res) => {
          this.pages = res.body;
        })
        .catch(() => {
          this.$swal("Error", "Failed to load pages", "error");
        })
        .finally(() => {
          this.loading = false;
        });
    },
    editPage(item) {
      this.$router.push({ name: "GoPagesV2Editor", params: { id: item.id } });
    },
    deletePage(item) {
      this.$swal({
        title: "Delete Page?",
        text: "This cannot be undone.",
        type: "warning",
        showCancelButton: true,
        confirmButtonText: "Delete",
        confirmButtonColor: "#d33",
        cancelButtonText: "Cancel",
      }).then((result) => {
        if (result.value) {
          this.$http
            .delete(`${this.ENDPOINT}/gopages/${item.id}`)
            .then(() => {
              fb.log("gopage_deleted", { pageId: item.id, slug: item.slug });
              this.pages = this.pages.filter((p) => p.id !== item.id);
              this.$swal({
                type: "success",
                title: "Deleted",
                text: "Page removed",
              });
            })
            .catch(() => {
              this.$swal({
                type: "error",
                title: "Error",
                text: "Failed to delete",
              });
            });
        }
      });
    },
    formatDate(d) {
      if (!d) return "—";
      const date = new Date(d);
      return date.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    },
    pLink(item) {
      if (process.env.VUE_APP_GOPAGES_DOMAIN) {
        return `${process.env.VUE_APP_GOPAGES_DOMAIN}/${item.slug}`;
      }
      var base = this.ENDPOINT
        ? this.ENDPOINT.replace("/api", "")
        : window.location.origin;
      return `${base}/p/${item.slug}`;
    },
    viewResults(item) {
      if (item.listsId) {
        this.$router.push({ name: "CRM", query: { listId: item.listsId } });
      } else {
        this.$router.push({ name: "CRM" });
      }
    },
    viewAnalytics(item) {
      this.$router.push({
        name: "GoPagesV2Analytics",
        params: { id: item.id },
        query: { pageName: item.businessName },
      });
    },
  },
};
</script>

<style scoped>
.dotted-gradient {
  background-image: linear-gradient(
    to right,
    #313131 40%,
    rgba(255, 255, 255, 0) 40%
  );
  background-position: bottom;
  background-size: 6px 2px;
  background-repeat: repeat-x;
  text-transform: inherit;
  padding-bottom: 2px;
  opacity: 0.7;
}
</style>
